import React, { useEffect, useState } from 'react';
import { Form, Skeleton } from 'antd';
import XIcon from '@assets/svgs/x-icon.svg';
import RedTick from '@assets/svgs/red-tick-icon.svg';
import UiButton from '@components/ui/button/UiButton';
import WhitePin from '@assets/svgs/white-pin.svg';
import ClosedIcon from '@assets/svgs/closed-position-icon.svg';
import PublishedIcon from '@assets/svgs/published-position-icon.svg';
import OpenIcon from '@assets/svgs/open-position-icon.svg';
import { ApplicantElement, CorrelationPositionsType } from '@models/applicants';
import {
  getCorrelatedPositions,
  getCorrelatedPositionsByTitle,
  postCorrelatedPositions,
} from '@services/applicants/applicants-http.service';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import { useTranslation } from 'next-i18next';
import styles from './ApplicantCorrelation.module.scss';

type ApplicantProps = {
  applicant: ApplicantElement;
  fetchApplicant?: () => void;
  setCorrelation: (value: boolean) => void;
};
const ApplicantCorrelation: React.FC<ApplicantProps> = ({ applicant, setCorrelation, fetchApplicant }) => {
  const { t: translate } = useTranslation('applicant');
  const [form] = Form.useForm();
  const [isChecked, setIsChecked] = useState(false);
  const [positions, setPositions] = useState<CorrelationPositionsType>([]);
  const [positionsByTitle, setPositionsByTitle] = useState<CorrelationPositionsType>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingByTitle, setLoadingByTitle] = useState<boolean>(false);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );
  const [applicantCorrelated] = useNotification(StateEnum.SUCCESS);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const positionsElem = await getCorrelatedPositions(applicant.id);
      setPositions(positionsElem);
      await setLoading(false);
    } catch (e: any) {
      generalError();
      setLoading(false);
    }
  };

  const fetchPositionsByTitle = async () => {
    setLoadingByTitle(true);
    try {
      const positionsElem = await getCorrelatedPositionsByTitle(applicant.id);
      setPositionsByTitle(positionsElem);
      await setLoadingByTitle(false);
    } catch (e: any) {
      generalError();
      setLoadingByTitle(false);
    }
  };

  useEffect(() => {
    fetchPositions();
    fetchPositionsByTitle();
  }, [applicant, form]);

  const handleDivClick = async (name: string) => {
    setIsChecked(!isChecked);
    await form.setFieldValue(name, !form.getFieldValue(name));
  };

  const handleSubmit = async (values: any) => {
    const checkedPositions = Object.keys(values)
      .filter((key) => values[key] === true)
      .map((key) => Number(key));

    try {
      const correlated: CorrelationPositionsType = await postCorrelatedPositions(applicant.id, checkedPositions);
      setCorrelation(false);
      if (fetchApplicant) fetchApplicant();
      correlated.map((position) => {
        if (position.sent) {
          applicantCorrelated(
            '',
            <div style={{ display: 'inline' }}>
              <div className="main-body-text" style={{ fontWeight: 'bold' }}>
                {`${applicant.firstName} ${applicant.lastName}`}
              </div>
              <div className="main-body-text">{translate('employee_assigned_to_position')}</div>
              <div className="main-body-text" style={{ fontWeight: 'bold' }}>
                {position.title}
              </div>
            </div>,
          );
        }
      });
    } catch (e: any) {
      generalError();
    }
  };

  const renderStatusIcon = (status: string) => {
    if (status === 'closed') return <ClosedIcon />;
    if (status === 'active') return <OpenIcon />;
    return <PublishedIcon />;
  };

  return (
    <div className={styles.wrapCorrelation}>
      <div className={styles.wrapCorrelationHeader}>
        <div className={`large-body-text ${styles.correlationHeader}`}>
          {translate('correlation')} {applicant.firstName} {applicant.lastName}
        </div>
        <div className={styles.wrapCloseIcon}>
          <XIcon onClick={() => setCorrelation(false)} />
        </div>
      </div>
      <Form onFinish={handleSubmit} form={form}>
        <div className={styles.wrapCorrelationsForm}>
          <div className={styles.wrapNewestPositions}>
            <div className={`small-body-text ${styles.correlationSubtitle}`}>{translate('recent_positions')}</div>
            {loading ? (
              <Skeleton active />
            ) : (
              positions.slice(0, 5).map((position) => {
                return (
                  <Form.Item key={position.id} name={position.id.toString()} className={styles.correlationFormItem}>
                    <div
                      role="presentation"
                      className={
                        !form.getFieldValue(position.id.toString())
                          ? styles.wrapPositionHeader
                          : styles.wrapPositionHeaderChecked
                      }
                      onClick={() => handleDivClick(position.id.toString())}
                    >
                      <div className={styles.wrapPositionText}>
                        {renderStatusIcon(position.status)}
                        <div className={`main-body-text ${styles.positionText}`}>{position.title}</div>
                      </div>
                      {!!form.getFieldValue(position.id.toString()) && <RedTick />}
                    </div>
                  </Form.Item>
                );
              })
            )}
          </div>
          <div className={`small-body-text ${styles.correlationSubtitle}`}>{translate('all_active_positions')}</div>
          {loadingByTitle ? (
            <Skeleton active />
          ) : (
            positionsByTitle.map((positionByTitle) => {
              return (
                <Form.Item
                  key={positionByTitle.id}
                  name={positionByTitle.id.toString()}
                  className={styles.correlationFormItem}
                >
                  <div
                    role="presentation"
                    className={
                      !form.getFieldValue(positionByTitle.id.toString())
                        ? styles.wrapPositionHeader
                        : styles.wrapPositionHeaderChecked
                    }
                    onClick={() => handleDivClick(positionByTitle.id.toString())}
                  >
                    <div className={styles.wrapPositionText}>
                      {renderStatusIcon(positionByTitle.status)}
                      <div className={`main-body-text ${styles.positionText}`}>{positionByTitle.title}</div>
                    </div>
                    {!!form.getFieldValue(positionByTitle.id.toString()) && <RedTick />}
                  </div>
                </Form.Item>
              );
            })
          )}
        </div>
        <div className={styles.footerActions}>
          <UiButton size="small" type="primary" icon={<WhitePin />} htmlType="submit" data-testid="submit-correlation">
            {translate('correlation')}
          </UiButton>
          <UiButton type="default" size="small" onClick={() => setCorrelation(false)}>
            {translate('cancel')}
          </UiButton>
        </div>
      </Form>
    </div>
  );
};

export default ApplicantCorrelation;
